const SERVER_URL = process.env.REACT_APP_SERVER_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

const TIER_LIMITS = {
    basic: {
        resume_builds: 1,
        score_checks: 5,
        job_matches: 3,
        cover_letters: 3,
        custom_templates: 1,
        pdf_export: false
    },
    pro: {
        resume_builds: Infinity,
        score_checks: 30,
        job_matches: Infinity,
        cover_letters: 20,
        custom_templates: 5,
        pdf_export: true
    },
    max: {
        resume_builds: Infinity,
        score_checks: Infinity,
        job_matches: Infinity,
        cover_letters: Infinity,
        custom_templates: 15,
        pdf_export: true
    }
};

// Credit costs per feature (only these features support credit overages)
const CREDIT_COSTS = {
    score_checks: 1,
    cover_letters: 2,
    custom_templates: 2
};

// Credit pack options for the purchase UI
const CREDIT_PACKS = [
    { id: 'starter', credits: 5, price: '$3', priceNum: 3, perCredit: '$0.60' },
    { id: 'value', credits: 15, price: '$8', priceNum: 8, perCredit: '$0.53', bestValue: true },
    { id: 'power', credits: 30, price: '$15', priceNum: 15, perCredit: '$0.50' }
];

class StripeService {
    // Fetch subscription status from server
    async getSubscription(email) {
        try {
            const res = await fetch(`${SERVER_URL}/api/stripe/subscription/${encodeURIComponent(email)}`);
            if (!res.ok) throw new Error('Failed to fetch subscription');
            const sub = await res.json();
            return sub;
        } catch (err) {
            console.error('[StripeService] getSubscription error:', err);
            // Return default basic tier on error
            return {
                tier: 'basic',
                status: 'active',
                usage: { resume_builds: 0, score_checks: 0, job_matches: 0, cover_letters: 0, custom_templates: 0 },
                credit_balance: 0,
                limits: TIER_LIMITS.basic
            };
        }
    }

    // Start a checkout session
    async createCheckoutSession(email, tier) {
        if (!['pro', 'max'].includes(tier)) throw new Error(`Invalid tier: ${tier}`);

        const res = await fetch(`${SERVER_URL}/api/stripe/create-checkout-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, tier })
        });

        if (!res.ok) {
            const text = await res.text();
            let msg = 'Failed to create checkout session';
            try { msg = JSON.parse(text).error || msg; } catch { msg = text || msg; }
            throw new Error(msg);
        }

        const { url } = await res.json();
        window.location.href = url; // Redirect to Stripe Checkout
    }

    // Start a credit pack checkout session (one-time payment)
    async createCreditCheckoutSession(email, pack) {
        if (!['starter', 'value', 'power'].includes(pack)) throw new Error(`Invalid pack: ${pack}`);

        const res = await fetch(`${SERVER_URL}/api/stripe/create-credit-checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, pack })
        });

        if (!res.ok) {
            const text = await res.text();
            let msg = 'Failed to create credit checkout session';
            try { msg = JSON.parse(text).error || msg; } catch { msg = text || msg; }
            throw new Error(msg);
        }

        const { url } = await res.json();
        window.location.href = url; // Redirect to Stripe Checkout
    }

    // Record usage for a feature
    async recordUsage(email, feature) {
        const res = await fetch(`${SERVER_URL}/api/stripe/record-usage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, feature })
        });

        const data = await res.json();

        if (!res.ok) {
            const err = Object.assign(
                new Error(data.error || 'Usage limit reached'),
                { limitReached: res.status === 403, ...data }
            );
            throw err;
        }

        return data;
    }

    // Open Stripe Customer Portal
    async openPortal(email) {
        const res = await fetch(`${SERVER_URL}/api/stripe/create-portal-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });

        if (!res.ok) {
            const text = await res.text();
            let msg = 'Failed to create portal session';
            try { msg = JSON.parse(text).error || msg; } catch { msg = text || msg; }
            throw new Error(msg);
        }

        const { url } = await res.json();
        window.location.href = url;
    }

    // Check if a feature can be used (client-side check)
    canUseFeature(sub, feature) {
        if (!sub) return false;
        const used = sub.usage?.[feature] || 0;
        const limit = sub.limits?.[feature] ?? 0;

        // Boolean feature gating
        if (typeof limit === 'boolean') return limit;

        // Unlimited feature
        if (limit === Infinity || limit === -1) return true;

        // Within tier limits
        if (used < limit) return true;

        // Over tier limit — check if credits can cover it
        const creditCost = CREDIT_COSTS[feature];
        if (creditCost && (sub.credit_balance || 0) >= creditCost) return true;

        return false;
    }

    // Check if feature is over tier limit but can be covered by credits
    isOverLimitWithCredits(sub, feature) {
        if (!sub) return false;
        const used = sub.usage?.[feature] || 0;
        const limit = sub.limits?.[feature] ?? 0;

        if (typeof limit === 'boolean') return false;
        if (limit === Infinity || limit === -1) return false;

        // Over tier limit AND has enough credits
        if (used >= limit) {
            const creditCost = CREDIT_COSTS[feature];
            return creditCost && (sub.credit_balance || 0) >= creditCost;
        }
        return false;
    }

    // Get remaining uses for a feature
    getRemainingUses(sub, feature) {
        if (!sub) return 0;
        const used = sub.usage?.[feature] || 0;
        const limit = sub.limits?.[feature] ?? 0;

        if (limit === Infinity || limit === -1) return Infinity;
        if (typeof limit === 'boolean') return limit ? Infinity : 0;

        return Math.max(0, limit - used);
    }

    // Get the limit for a feature based on tier
    getFeatureLimit(tier, feature) {
        const limits = TIER_LIMITS[tier] || TIER_LIMITS.basic;
        return limits[feature];
    }

    // Check if a feature supports credit overages
    isCreditEligible(feature) {
        return !!CREDIT_COSTS[feature];
    }

    // Get credit cost for a feature
    getCreditCost(feature) {
        return CREDIT_COSTS[feature] || 0;
    }
}

export const stripeService = new StripeService();
export { TIER_LIMITS, CREDIT_COSTS, CREDIT_PACKS };
